import { LIMIARES } from './youtubeMetrics';

/**
 * O score de um canal, a partir dos vídeos já carregados.
 *
 * Espelho de `backend/services/canalScore.js`, exatamente como
 * `utils/seoScore.js` espelha o de site. O backend é a fonte: é lá que a nota é
 * calculada e gravada em `runs.seo_score` ao fim de cada coleta. Aqui a mesma
 * regra roda sobre os vídeos em memória, o que permite ao painel responder ao
 * filtro — ver a nota de um recorte é o que a tela oferece e o relatório não.
 *
 * As duas metades são amarradas por `scripts/verificarScore.js` aqui e por um
 * teste de mesmo nome no backend, que usam os mesmos casos.
 */

/** Os mesmos pesos do backend, na mesma ordem das abas do painel. */
export const PESOS = { metadados: 35, engajamento: 30, desempenho: 25, publicacao: 10 };

/** Abaixo disto, a aba Desempenho já trata o vídeo como ponto de atenção. */
export const VIEWS_BAIXAS = 1000;

const num = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

const texto = (valor) => (typeof valor === 'string' ? valor.trim() : '');

/**
 * Quantos vídeos têm **pelo menos um** problema em cada área.
 *
 * Não é a conta de `problemasPorSecao`, que soma ocorrências para o contador
 * das abas — lá um vídeo sem descrição, com título longo e sem thumbnail entra
 * três vezes. Aqui entra uma: é um vídeo para arrumar, não três.
 *
 * Os predicados espelham o `FILTER` de `SQL_CONTAGENS` no backend, um a um.
 */
export const contagensPorArea = (videos = []) => {
  const conta = (teste) => videos.filter(teste).length;

  return {
    total: videos.length,
    metadados: conta(
      (v) =>
        texto(v.description).length < LIMIARES.DESCRICAO_MIN ||
        (v.title || '').length > LIMIARES.TITULO_MAX ||
        texto(v.thumbnail_url) === ''
    ),
    // Vídeo sem visualização fica de fora: taxa exige denominador, e chamar de
    // pouco engajado quem ninguém viu mediria alcance duas vezes.
    engajamento: conta(
      (v) =>
        num(v.views) > 0 &&
        ((num(v.likes) + num(v.comments)) / num(v.views)) * 100 < LIMIARES.ENGAJAMENTO_BOM
    ),
    desempenho: conta((v) => num(v.views) < VIEWS_BAIXAS),
    publicacao: conta((v) => !v.published_at)
  };
};

/**
 * `100 − Σ(peso × afetados / total)`.
 *
 * `null` sem vídeo nenhum, e não zero: zero é uma medição, e "não há o que
 * medir" não é.
 */
export const calcularScore = (contagens) => {
  const total = Number(contagens?.total) || 0;
  if (total === 0) return null;

  const perda = Object.entries(PESOS).reduce((soma, [area, peso]) => {
    const afetados = Math.min(total, Number(contagens[area]) || 0);
    return soma + peso * (afetados / total);
  }, 0);

  return Math.max(0, Math.min(100, Math.round(100 - perda)));
};

/** Atalho: dos vídeos direto ao número. */
export const scoreDosVideos = (videos) => calcularScore(contagensPorArea(videos));
